/**
 * Order Filters Component
 *
 * Filters for the purchase orders list.
 * @see US-8.14 - Page Liste des Commandes
 */

import { Select } from '@/components/ui';
import { DatePicker, type DateRange } from '@/components/ui';
import { useActiveSuppliers } from '@/features/suppliers/hooks/useSuppliers';
import { PurchaseOrderStatus, PURCHASE_ORDER_STATUS_LABELS } from '@bstock/shared';

interface OrderFiltersProps {
  supplierId: string;
  status: string;
  dateRange: DateRange;
  onSupplierChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onDateRangeChange: (range: DateRange) => void;
  /** Si false, le filtre fournisseur est masqué (évite 403 pour OPERATOR) */
  showSupplierFilter?: boolean;
}

const statusOptions = [
  { value: PurchaseOrderStatus.DRAFT, label: PURCHASE_ORDER_STATUS_LABELS[PurchaseOrderStatus.DRAFT] },
  { value: PurchaseOrderStatus.SENT, label: PURCHASE_ORDER_STATUS_LABELS[PurchaseOrderStatus.SENT] },
  { value: PurchaseOrderStatus.PARTIALLY_RECEIVED, label: PURCHASE_ORDER_STATUS_LABELS[PurchaseOrderStatus.PARTIALLY_RECEIVED] },
  { value: PurchaseOrderStatus.RECEIVED, label: PURCHASE_ORDER_STATUS_LABELS[PurchaseOrderStatus.RECEIVED] },
  { value: PurchaseOrderStatus.CANCELLED, label: PURCHASE_ORDER_STATUS_LABELS[PurchaseOrderStatus.CANCELLED] },
];

export function OrderFilters({
  supplierId,
  status,
  dateRange,
  onSupplierChange,
  onStatusChange,
  onDateRangeChange,
  showSupplierFilter = true,
}: OrderFiltersProps) {
  // Ne charger les fournisseurs que si le filtre est affiché (évite 403)
  const { data: suppliers, isLoading: suppliersLoading } = useActiveSuppliers({
    enabled: showSupplierFilter,
  });

  const supplierOptions = suppliers?.map((s) => ({ value: s.id, label: s.name })) ?? [];

  return (
    <div className="flex flex-wrap items-end gap-4">
      {/* Date Range - First */}
      <div className="w-72">
        <label className="block text-sm font-medium text-dark-500 mb-1.5">
          Periode
        </label>
        <DatePicker
          value={dateRange}
          onChange={onDateRangeChange}
          placeholder="Selectionner une periode"
        />
      </div>

      {/* Supplier - MANAGER+ uniquement */}
      {showSupplierFilter && (
        <div className="w-52">
          <Select
            label="Fournisseur"
            value={supplierId}
            onChange={onSupplierChange}
            options={supplierOptions}
            disabled={suppliersLoading}
            placeholder="Tous les fournisseurs"
          />
        </div>
      )}

      {/* Status */}
      <div className="w-44">
        <Select
          label="Statut"
          value={status}
          onChange={onStatusChange}
          options={statusOptions}
          placeholder="Tous les statuts"
        />
      </div>
    </div>
  );
}
